import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import HotelIcon from '@mui/icons-material/Hotel';
import { paymentService } from '../../services';

const PaymentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State for payment data
  const [payment, setPayment] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Status update dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [updating, setUpdating] = useState(false);
  
  // Fetch payment data
  const fetchPaymentData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch payment details
      const paymentData = await paymentService.getPaymentById(id);
      setPayment(paymentData);

      // Customer data is already populated by the backend via .populate()
      // paymentData.customerId contains the full customer object, not just the ID
      if (paymentData.customerId) {
        setCustomer(paymentData.customerId);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch payment details');
    } finally {
      setLoading(false);
    }
  };
  
  // Load payment data on initial render
  useEffect(() => {
    fetchPaymentData();
  }, [id]);
  
  // Handle status dialog open
  const handleStatusDialogOpen = () => {
    if (payment) {
      setNewStatus(payment.status);
      setStatusDialogOpen(true);
    }
  };
  
  // Handle status dialog close
  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
    setStatusNote('');
  };
  
  // Handle status update
  const handleStatusUpdate = async () => {
    setUpdating(true);
    
    try {
      await paymentService.updatePaymentStatus(id, newStatus);
      
      // Refresh payment data
      await fetchPaymentData();
      
      // Close dialog
      handleStatusDialogClose();
    } catch (err) {
      setError(err.message || 'Failed to update payment status');
    } finally {
      setUpdating(false);
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Format currency
  const formatCurrency = (amount, currency = 'THB') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  
  // Get status chip color
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/payments')}
          sx={{ mt: 2 }}
        >
          Back to Payments
        </Button>
      </Container>
    );
  }
  
  if (!payment) {
    return (
      <Container maxWidth="lg">
        <Alert severity="info" sx={{ mt: 4 }}>
          Payment not found
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/payments')}
          sx={{ mt: 2 }}
        >
          Back to Payments
        </Button>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link to="/admin" style={{ textDecoration: 'none', color: 'inherit' }}>
          Dashboard
        </Link>
        <Link to="/admin/payments" style={{ textDecoration: 'none', color: 'inherit' }}>
          Payments
        </Link>
        <Typography color="text.primary">
          {payment.referenceNumber || 'Payment Details'}
        </Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            sx={{ mr: 2 }}
            onClick={() => navigate('/admin/payments')}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Payment Details
          </Typography>
          <Chip 
            label={payment.status} 
            color={getStatusColor(payment.status)}
            size="medium"
            sx={{ ml: 2 }}
          />
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<EditIcon />}
          onClick={handleStatusDialogOpen}
        >
          Update Status
        </Button>
      </Box>
      
      {/* Payment details */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          {/* Payment summary card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <AttachMoneyIcon sx={{ color: 'primary.main', mr: 1, fontSize: 32 }} />
                <Typography variant="h5">
                  {formatCurrency(payment.amount, payment.currency)}
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Reference Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {payment.referenceNumber || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Payment Method
                  </Typography>
                  <Typography variant="body1">
                    {payment.paymentMethod}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(payment.createdAt)}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip 
                    label={payment.status} 
                    color={getStatusColor(payment.status)}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          {/* Customer info card */}
          {customer && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ color: 'primary.main', mr: 1, fontSize: 32 }} />
                  <Typography variant="subtitle1">
                    {`${customer.firstName} ${customer.lastName}`}
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {customer.email}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">
                      {customer.phone || 'Not provided'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body1">
                      {customer.country}{(customer.province || customer.state) ? `, ${customer.province || customer.state}` : ''}
                    </Typography>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/admin/customers/${customer._id}`)}
                  >
                    View Customer
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* BOGO Attendee info card */}
          {payment.pricingType === 'bogo' && (
            <Card sx={{ mt: 3, borderColor: 'primary.main', borderWidth: 2 }} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PeopleIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6">
                    BOGO Registration
                  </Typography>
                  <Chip
                    label="2 Attendees"
                    color="primary"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />

                {/* Attendee 1 */}
                {payment.attendees && payment.attendees[0] && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Attendee 1
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', textTransform: 'capitalize' }}>
                      {payment.attendees[0].firstName} {payment.attendees[0].lastName}
                    </Typography>
                  </Box>
                )}

                {/* Attendee 2 */}
                {payment.attendees && payment.attendees[1] && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Attendee 2
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', textTransform: 'capitalize' }}>
                      {payment.attendees[1].firstName} {payment.attendees[1].lastName}
                    </Typography>
                  </Box>
                )}

                {/* Accommodation Preference */}
                {payment.accommodationPreference && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <HotelIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary">
                        Accommodation Preference
                      </Typography>
                    </Box>
                    <Chip
                      label={
                        payment.accommodationPreference === 'same_room'
                          ? 'Same Room (1 Bed Shared)'
                          : payment.accommodationPreference === 'shared_two_beds'
                          ? 'Shared Room (2 Beds)'
                          : 'Separate Rooms'
                      }
                      color={
                        payment.accommodationPreference === 'same_room'
                          ? 'info'
                          : payment.accommodationPreference === 'shared_two_beds'
                          ? 'primary'
                          : 'warning'
                      }
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
        
        <Grid item xs={12} md={8}>
          {/* Payment details card */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment Details
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Transaction ID
                </Typography>
                <Typography variant="body1">
                  {payment.transactionId || 'N/A'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Payment Type
                </Typography>
                <Typography variant="body1">
                  {payment.paymentType || payment.paymentMethod}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Amount
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(payment.amount, payment.currency)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Currency
                </Typography>
                <Typography variant="body1">
                  {payment.currency === 'CAD' ? 'CAD (Canadian Dollar)' : 'USD (US Dollar)'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Payment Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(payment.createdAt)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body1">
                  {formatDate(payment.updatedAt)}
                </Typography>
              </Grid>
              
              {payment.paymentDetails && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Payment Details
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        {Object.entries(payment.paymentDetails).map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell component="th" scope="row" sx={{ width: '40%' }}>
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                            </TableCell>
                            <TableCell>{value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
              
              {payment.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Notes
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body1">
                      {payment.notes}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Paper>
          
          {/* Payment history */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment History
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {payment.statusHistory?.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payment.statusHistory.map((history, index) => (
                      <TableRow key={index}>
                        <TableCell>{formatDate(history.date)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={history.status} 
                            color={getStatusColor(history.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{history.note || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CalendarTodayIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No status history available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* Status update dialog */}
      <Dialog open={statusDialogOpen} onClose={handleStatusDialogClose}>
        <DialogTitle>Update Payment Status</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Change the status of this payment. This will update the payment record and add an entry to the status history.
          </DialogContentText>
          <TextField
            select
            fullWidth
            label="New Status"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            margin="dense"
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="refunded">Refunded</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Note (Optional)"
            value={statusNote}
            onChange={(e) => setStatusNote(e.target.value)}
            margin="dense"
            multiline
            rows={2}
            placeholder="Add a note about this status change"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStatusDialogClose}>Cancel</Button>
          <Button 
            onClick={handleStatusUpdate} 
            variant="contained"
            disabled={updating}
          >
            {updating ? <CircularProgress size={24} /> : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaymentDetail;